// src/security/SEC_03_02_RolePermissionGuard.jsx
import React from "react";
import { useAuth } from "..";
import { useSecurity } from "./SEC_03_03_SecurityContext";

/**
 * Generic authorization gate for Registered workspace + domains.
 */
export default function SEC_03_02_RolePermissionGuard({
  children,
  roles = null,          // array of required roles (ANY-of)
  requiredPermission = null, // specific capabilityCode needed
  corpEntityId = null,   // required corp entity scope (mapped to divisionCode)
  denyTitle = "Access restricted",
}) {
  const { user, isInitialising: authLoading } = useAuth();
  const { 
    roles: profileRoles, 
    isGlobalAdmin, 
    can, 
    isInitialising: securityLoading 
  } = useSecurity();

  // 🎯 METHODOLOGY: Wait for both Auth and Security Matrix to hydrate
  if (authLoading || securityLoading) {
    return (
      <div style={{ padding: "1.5rem" }}>
        <p>Checking your access matrix…</p>
      </div>
    );
  }

  // 1. AUTH CHECK
  if (!user) {
    return (
      <div style={{ padding: "1.5rem" }}>
        <h2>Sign-in required</h2>
        <p>You must be signed in to access this workspace.</p>
      </div>
    );
  }

  // 2. GLOBAL ADMIN BYPASS
  // If the matrix contains 'SITEADMIN' or 'GLOBALADMIN', they pass all checks.
  if (isGlobalAdmin) {
    return <>{children}</>;
  }

  // 3. ROLE CHECK (ANY-OF)
  const hasAnyRole =
    !roles ||
    roles.some(r => profileRoles.includes(String(r).toUpperCase()));

  // 4. PERMISSION & DIVISION CHECK
  // We use the 'can' function which scans the matrix capabilityCode and divisionCode
  const hasPermission = !requiredPermission || can(requiredPermission, corpEntityId);

  // 5. FINAL VALIDATION
  if (!hasAnyRole || !hasPermission) {
    return (
      <div style={{ padding: "1.5rem" }}>
        <h2>{denyTitle}</h2>
        <p>
          Signed in as <strong>{user.userName || user.email}</strong>, but you don’t have permission to access this area.
        </p>
        <div style={{ marginTop: "0.75rem", opacity: 0.8, fontSize: "0.85rem" }}>
          <p><strong>Security Requirements:</strong></p>
          <ul>
            {roles && <li>Required Roles: {roles.join(", ")}</li>}
            {requiredPermission && <li>Required Capability: {requiredPermission}</li>}
            {corpEntityId && <li>Required Division: {corpEntityId}</li>}
          </ul>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export const RolePermissionGuard = SEC_03_02_RolePermissionGuard;